import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { m, AnimatePresence } from 'framer-motion';
import { HiMenu, HiX } from 'react-icons/hi';
import { FiSun, FiMoon, FiGlobe } from 'react-icons/fi';

const navKeys = ['about', 'experience', 'skills', 'projects', 'contact'];

function Header() {
  const { t, i18n } = useTranslation();
  const { isDark, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
  let ticking = false;
  const handleScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 20);
        ticking = false;
      });
      ticking = true;
    }
  };
  window.addEventListener('scroll', handleScroll, { passive: true });
  return () => window.removeEventListener('scroll', handleScroll);
}, []);


  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'en' ? 'es' : 'en');
  };

  const scrollToSection = (sectionId) => {
    setIsOpen(false);
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 350);
  };

  return (
    <m.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50  ${
        scrolled
          ? 'bg-white/90 dark:bg-gray-900/90 shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <nav className="section-container flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <m.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="cursor-pointer"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <h1 className={`text-xl md:text-2xl font-bold transition-colors duration-200 ${
            scrolled
              ? 'text-gray-900 dark:text-white'
              : 'text-white'
          }`}>
            Juan<span className="gradient-text">Zdev</span>
          </h1>
        </m.div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {navKeys.map((key) => (
            <m.button
              key={key}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                scrolled
                  ? 'text-gray-700 dark:text-gray-300 hover:text-primary-500 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              {t(`nav.${key}`)}
            </m.button>
          ))}

          {/* Language Toggle */}
          <m.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleLanguage}
            className={`ml-2 p-2 rounded-lg transition-all duration-200 ${
              scrolled
                ? 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                : 'text-white/80 hover:text-white hover:bg-white/10'
            }`}
            title={i18n.language === 'en' ? 'Cambiar a Espa\u00f1ol' : 'Switch to English'}
          >
            <FiGlobe className="w-5 h-5" />
          </m.button>

          {/* Theme Toggle */}
          {/* <m.button
            whileHover={{ scale: 1.1, rotate: 180 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleTheme}
            className={`p-2 rounded-lg  ${
              scrolled
                ? 'text-gray-600 dark:text-gray-400 '
                : 'text-white/80 '
            }`}
          >
            {isDark ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
          </m.button> */}
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-2 md:hidden">
          <m.button
            whileTap={{ scale: 0.9 }}
            onClick={toggleLanguage}
            className={`p-2 rounded-lg ${
              scrolled ? 'text-gray-600 dark:text-gray-400' : 'text-white'
            }`}
          >
            <FiGlobe className="w-5 h-5" />
          </m.button>
          {/* <m.button
            whileTap={{ scale: 0.9 }}
            onClick={toggleTheme}
            className={`p-2 rounded-lg ${
              scrolled ? 'text-gray-600 dark:text-gray-400' : 'text-white'
            }`}
          >
            {isDark ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
          </m.button> */}
          <m.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsOpen(!isOpen)}
            className={`p-2 rounded-lg ${
              scrolled ? 'text-gray-600 dark:text-gray-400' : 'text-white'
            }`}
          >
            {isOpen ? <HiX className="w-6 h-6" /> : <HiMenu className="w-6 h-6" />}
          </m.button>
        </div>
      </nav>

      {/* Mobile Menfdshauuu */}
      <AnimatePresence>
        {isOpen && (
          <m.div
            key="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-white/95 dark:bg-gray-900/95 border-t border-gray-200/50 dark:border-gray-700/50"
          >
            <div className="section-container py-4 flex flex-col gap-1">
              {navKeys.map((key, index) => (
                <m.button
                  key={key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => scrollToSection(key)}
                  className="text-right px-12 py-3 rounded-lg text-gray-700 dark:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-500 font-medium "
                >
                  {t(`nav.${key}`)}
                </m.button>
              ))}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </m.header>
  );
}

export default Header;
